import {
  getFirstClearHovCoreReward,
  WALL_DEFINITIONS,
  type WallFirstClearReward,
  type WallId,
} from "./wallDefinitions";
import {
  getWallChallengePolicy,
  type WallRewardEffect,
} from "./wallChallengePolicy";
import {
  DEFAULT_WALL_REWARD_CAP_LEVEL,
  type WallRewardCapLevel,
  type WallRewardEffectiveCap,
} from "./wallRewardCap";
import type { WallState } from "./wallState";
import {
  normalizeMpWallAssignments,
  type MpWallAssignments,
} from "./mpWallAssignments";

export interface WallSnapshotEntry {
  readonly id: WallId;
  readonly active: boolean;
  // True when this wall has met its progression unlock gate and the player
  // can see and challenge it. Wall1 (`step-wall`) is always available once the
  // Wall surface is unlocked; later walls gate on prior-wall clears.
  readonly challengeAvailable: boolean;
  readonly bestClearMs: number | null;
  // Authoritative typed reward effect for this wall. UI, PlayLog, and balance
  // analysis consumers should switch on `operation`/`dimension` rather than
  // assuming a multiplier — additive effects (e.g. Freshness Wall's MHov
  // exponent bonus) must not be displayed or aggregated as multipliers.
  readonly rewardEffect: WallRewardEffect;
  // Legacy multiplier mirror. Equals `rewardEffect.value` for `multiply`
  // effects and 1 for `add` effects so it never carries the "1 + bonus"
  // display fiction; new code should consume `rewardEffect` directly.
  readonly rewardMultiplier: number;
  // Effective upper bound currently applied to `rewardEffect`, or null when
  // the wall's reward is unbounded. `reached` is the authoritative cap-reached
  // state; consumers must not re-derive it from wall-specific constants.
  readonly rewardEffectiveCap: WallRewardEffectiveCap | null;
  // Game-speed-adjusted elapsed for the active run. Sourced from
  // `WallState.wallRunElapsedMs`, which the model advances via `update(dt)`
  // so admin/system speed multipliers compress the displayed elapsed too.
  readonly currentRunElapsedMs: number | null;
  readonly firstClearReward: WallFirstClearReward;
  readonly firstClearHovCoreReward: number | null;
  // True once the wall has been cleared at least once. Latches with
  // `hasClearRecord`, which is the same condition the model uses to suppress
  // re-granting the first-clear reward on subsequent clears.
  readonly firstClearRewardClaimed: boolean;
}

export interface WallSnapshot {
  readonly wallUnlocked: boolean;
  readonly activeWallId: WallId | null;
  readonly mpWallAssignments: MpWallAssignments | null;
  readonly walls: readonly WallSnapshotEntry[];
  readonly unseenClearedWallIds: readonly WallId[];
}

export interface CreateWallSnapshotOptions {
  readonly state: WallState;
  readonly wallUnlocked: boolean;
  readonly rewardCapLevel?: WallRewardCapLevel;
}

export function createWallSnapshot(
  options: CreateWallSnapshotOptions,
): WallSnapshot {
  const {
    state,
    wallUnlocked,
    rewardCapLevel = DEFAULT_WALL_REWARD_CAP_LEVEL,
  } = options;
  const walls = WALL_DEFINITIONS.map<WallSnapshotEntry>((definition) => {
    const policy = getWallChallengePolicy(definition.id);
    const active = state.activeWallId === definition.id;
    const challengeAvailable = policy.isChallengeAvailable(state);
    const bestClearMs = policy.resolveBestClearMs(state);
    const rewardEffect = policy.resolveRewardEffect(state, rewardCapLevel);
    const rewardEffectiveCap = policy.resolveRewardEffectiveCap(
      state,
      rewardCapLevel,
    );
    const rewardMultiplier =
      rewardEffect.operation === "multiply" ? rewardEffect.value : 1;
    const currentRunElapsedMs =
      active && state.wallRunElapsedMs !== null
        ? Math.max(0, state.wallRunElapsedMs)
        : null;
    return {
      id: definition.id,
      active,
      challengeAvailable,
      bestClearMs,
      rewardEffect,
      rewardMultiplier,
      rewardEffectiveCap,
      currentRunElapsedMs,
      firstClearReward: definition.firstClearReward,
      firstClearHovCoreReward: getFirstClearHovCoreReward(definition.id),
      firstClearRewardClaimed: policy.hasClearRecord(state),
    };
  });
  return {
    wallUnlocked,
    activeWallId: state.activeWallId,
    mpWallAssignments:
      state.activeWallId === "mp-wall"
        ? normalizeMpWallAssignments(state.mpWallAssignments)
        : null,
    walls,
    unseenClearedWallIds: [...state.unseenClearedWallIds],
  };
}
